import type { MapTileDataJson, RiverSegmentJson, LakeOverlayJson } from '../platform/api';
import type { ChunkCoordJson } from '../platform/api';
import type { BiomeKind } from '../generated/BiomeKind';

const CELL_SIZE = 8;

const BIOME_COLORS: Record<BiomeKind, [number, number, number]> = {
  Ocean: [0x1a, 0x47, 0x6a],
  Coast: [0xc4, 0xaa, 0x78],
  Grassland: [0x6b, 0x8e, 0x5e],
  Forest: [0x2a, 0x54, 0x3a],
  Desert: [0xd4, 0xb8, 0x82],
  Tundra: [0xc8, 0xd0, 0xc8],
  Swamp: [0x4a, 0x5a, 0x38],
  Mountain: [0x78, 0x78, 0x78],
};

const ZENITH = (45 * Math.PI) / 180;
const AZIMUTH = (315 * Math.PI) / 180;
const COS_Z = Math.cos(ZENITH);
const SIN_Z = Math.sin(ZENITH);

function hillshadeBrightnessLocal(
  tile: MapTileDataJson,
  tiles: MapTileDataJson[][],
  rx: number,
  ry: number,
): number {
  const eL = rx > 0 ? tiles[ry]?.[rx - 1]?.elevation : undefined;
  const eR = tiles[ry]?.[rx + 1]?.elevation;
  const eU = ry > 0 ? tiles[ry - 1]?.[rx]?.elevation : undefined;
  const eD = tiles[ry + 1]?.[rx]?.elevation;

  if (eL == null || eR == null || eU == null || eD == null) {
    return 0.65 + tile.elevation * 0.35;
  }

  const dzdx = (eR - eL) / 2;
  const dzdy = (eD - eU) / 2;
  const slope = Math.sqrt(dzdx * dzdx + dzdy * dzdy);
  const aspect = Math.atan2(dzdy, dzdx);
  const hs = COS_Z + SIN_Z * slope * Math.cos(AZIMUTH - aspect);
  return Math.max(0.65, Math.min(1.0, hs));
}

function tileRgbSimple(tile: MapTileDataJson): [number, number, number] {
  const base = BIOME_COLORS[tile.biome] ?? [128, 128, 128];
  const brightness = 0.65 + tile.elevation * 0.35;
  return [
    Math.min(255, Math.round(base[0] * brightness)),
    Math.min(255, Math.round(base[1] * brightness)),
    Math.min(255, Math.round(base[2] * brightness)),
  ];
}

function tileRgbHillshade(
  tile: MapTileDataJson,
  tiles: MapTileDataJson[][],
  rx: number,
  ry: number,
): [number, number, number] {
  const base = BIOME_COLORS[tile.biome] ?? [128, 128, 128];
  const brightness = hillshadeBrightnessLocal(tile, tiles, rx, ry);
  return [
    Math.min(255, Math.round(base[0] * brightness)),
    Math.min(255, Math.round(base[1] * brightness)),
    Math.min(255, Math.round(base[2] * brightness)),
  ];
}

function renderRegionBitmap(
  tiles: MapTileDataJson[][],
  hillshade: boolean,
): ImageData {
  const h = tiles.length;
  const w = tiles[0]?.length ?? 0;
  const data = new Uint8ClampedArray(w * h * 4);
  for (let ry = 0; ry < h; ry++) {
    const row = tiles[ry]!;
    for (let rx = 0; rx < w; rx++) {
      const tile = row[rx]!;
      const [r, g, b] = hillshade
        ? tileRgbHillshade(tile, tiles, rx, ry)
        : tileRgbSimple(tile);
      const idx = (ry * w + rx) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = 255;
    }
  }
  return new ImageData(data, w, h);
}

export interface BoundedRiver extends RiverSegmentJson {
  bbox: { x1: number; y1: number; x2: number; y2: number };
}

export interface BoundedLake extends LakeOverlayJson {
  bbox: { x1: number; y1: number; x2: number; y2: number };
}

export function computeRiverBounds(river: RiverSegmentJson): BoundedRiver {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const pt of river.points) {
    if (pt.x < x1) x1 = pt.x;
    if (pt.y < y1) y1 = pt.y;
    if (pt.x > x2) x2 = pt.x;
    if (pt.y > y2) y2 = pt.y;
  }
  return { ...river, bbox: { x1, y1, x2, y2 } };
}

export function computeLakeBounds(lake: LakeOverlayJson): BoundedLake {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const cell of lake.cells) {
    if (cell.x < x1) x1 = cell.x;
    if (cell.y < y1) y1 = cell.y;
    if (cell.x > x2) x2 = cell.x;
    if (cell.y > y2) y2 = cell.y;
  }
  return { ...lake, bbox: { x1, y1, x2, y2 } };
}

const RIVER_COLOR = 'rgba(65, 135, 200, 0.85)';
const LAKE_COLOR = 'rgba(45, 100, 160, 0.7)';
const RIVER_ZOOM_THRESHOLD = 0.5;

export type LayerName = 'terrain' | 'hydro' | 'claims' | 'ui';

export interface ViewState {
  offsetX: number;
  offsetY: number;
  zoom: number;
}

export interface CachedRegion {
  x1: number;
  y1: number;
  tiles: MapTileDataJson[][];
  canvas: HTMLCanvasElement | null;
  canvasHillshade: boolean;
}

interface ClaimBitmap {
  canvas: OffscreenCanvas;
  originX: number;
  originY: number;
}

export interface RenderState {
  view: ViewState;
  regions: Map<string, CachedRegion>;
  rivers: BoundedRiver[];
  lakes: BoundedLake[];
  claimBitmaps: { own: ClaimBitmap | null; other: ClaimBitmap | null };
  hoverChunk: ChunkCoordJson | null;
  selectedChunk: ChunkCoordJson | null;
  showGrid: boolean;
  showPanel: boolean;
  inExpansion: boolean;
  expandingChunks: Set<string>;
  pendingAdditions: Set<string>;
  pendingRemovals: Set<string>;
  validationErrors: ChunkCoordJson[];
  islandChunks: ChunkCoordJson[];
  disconnected: Set<string>;
  activeTool: string;
  brushSize: number;
  brushPreviewChunks: ChunkCoordJson[];
  fillPreviewChunks: ChunkCoordJson[];
  worldW: number;
  worldH: number;
  isBlockAreaClaimed: (bx: number, by: number) => boolean;
  allClaimedSet: () => Set<string>;
  tileLookup: (cx: number, cy: number) => MapTileDataJson | null;
}

export interface MapRenderer {
  invalidate(layer: LayerName): void;
  frame(state: RenderState): void;
  composite(view: ViewState): void;
  resize(width: number, height: number): void;
  chunkToScreen(cx: number, cy: number, view: ViewState): { x: number; y: number };
  destroy(): void;
}

export function createMapRenderer(container: HTMLElement): MapRenderer {
  const canvases: Record<LayerName, HTMLCanvasElement> = {
    terrain: document.createElement('canvas'),
    hydro: document.createElement('canvas'),
    claims: document.createElement('canvas'),
    ui: document.createElement('canvas'),
  };

  const dirty: Record<LayerName, boolean> = {
    terrain: true,
    hydro: true,
    claims: true,
    ui: true,
  };

  const layerOrder: LayerName[] = ['terrain', 'hydro', 'claims', 'ui'];

  for (const name of layerOrder) {
    const c = canvases[name];
    c.className = 'mapLayer';
    c.style.position = 'absolute';
    c.style.inset = '0';
    c.style.pointerEvents = name === 'ui' ? 'auto' : 'none';
    container.appendChild(c);
  }

  function invalidate(layer: LayerName): void {
    dirty[layer] = true;
  }

  function resize(width: number, height: number): void {
    for (const name of layerOrder) {
      canvases[name].width = width;
      canvases[name].height = height;
      dirty[name] = true;
    }
  }

  function chunkToScreen(
    cx: number,
    cy: number,
    view: ViewState,
  ): { x: number; y: number } {
    const cellPx = CELL_SIZE * view.zoom;
    return {
      x: view.offsetX + cx * cellPx,
      y: view.offsetY + cy * cellPx,
    };
  }

  function renderTerrain(state: RenderState): void {
    const ctx = canvases.terrain.getContext('2d');
    if (!ctx) return;
    const { view, regions } = state;
    const cellPx = CELL_SIZE * view.zoom;
    const w = canvases.terrain.width;
    const h = canvases.terrain.height;

    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;

    const useHillshade = view.zoom >= 1.0;

    for (const region of regions.values()) {
      const tiles = region.tiles;
      if (tiles.length === 0) continue;
      const rw = tiles[0]!.length;
      const rh = tiles.length;

      const rx1 = view.offsetX + region.x1 * cellPx;
      const ry1 = view.offsetY + region.y1 * cellPx;
      const rw_px = rw * cellPx;
      const rh_px = rh * cellPx;
      if (rx1 + rw_px < 0 || rx1 > w || ry1 + rh_px < 0 || ry1 > h) continue;

      if (!region.canvas || region.canvasHillshade !== useHillshade) {
        const img = renderRegionBitmap(tiles, useHillshade);
        const off = document.createElement('canvas');
        off.width = rw;
        off.height = rh;
        off.getContext('2d')!.putImageData(img, 0, 0);
        region.canvas = off;
        region.canvasHillshade = useHillshade;
      }
      ctx.drawImage(region.canvas, rx1, ry1, rw_px, rh_px);
    }
  }
  function renderHydro(state: RenderState): void {
    const ctx = canvases.hydro.getContext('2d');
    if (!ctx) return;
    const { view, rivers, lakes } = state;
    const cellPx = CELL_SIZE * view.zoom;
    const w = canvases.hydro.width;
    const h = canvases.hydro.height;

    if (view.zoom < RIVER_ZOOM_THRESHOLD) return;

    // Viewport in chunk coords
    const vx1 = -view.offsetX / cellPx;
    const vy1 = -view.offsetY / cellPx;
    const vx2 = (w - view.offsetX) / cellPx;
    const vy2 = (h - view.offsetY) / cellPx;

    // Lakes
    ctx.fillStyle = LAKE_COLOR;
    for (const lake of lakes) {
      if (lake.bbox.x2 < vx1 || lake.bbox.x1 > vx2 ||
          lake.bbox.y2 < vy1 || lake.bbox.y1 > vy2) continue;
      for (const cell of lake.cells) {
        const px = view.offsetX + cell.x * cellPx;
        const py = view.offsetY + cell.y * cellPx;
        if (px + cellPx < 0 || px > w || py + cellPx < 0 || py > h) continue;
        ctx.fillRect(px, py, cellPx + 0.5, cellPx + 0.5);
      }
    }

    // Rivers
    ctx.strokeStyle = RIVER_COLOR;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const river of rivers) {
      if (river.bbox.x2 < vx1 || river.bbox.x1 > vx2 ||
          river.bbox.y2 < vy1 || river.bbox.y1 > vy2) continue;
      if (river.points.length < 2) continue;

      let segStart = 0;
      for (let i = 1; i <= river.points.length; i++) {
        const prev = river.points[i - 1]!;
        const cur = i < river.points.length ? river.points[i]! : null;
        const jump = cur != null && Math.abs(cur.x - prev.x) > 500;

        if (jump || cur == null) {
          if (i - segStart >= 2) {
            ctx.beginPath();
            for (let j = segStart; j < i; j++) {
              const pt = river.points[j]!;
              const sx = view.offsetX + pt.x * cellPx;
              const sy = view.offsetY + pt.y * cellPx;
              const lw = Math.max(
                0.5,
                Math.min(6, Math.sqrt(pt.accumulation) * view.zoom * 0.02),
              );
              ctx.lineWidth = lw;
              if (j === segStart) ctx.moveTo(sx, sy);
              else ctx.lineTo(sx, sy);
            }
            ctx.stroke();
          }
          segStart = i;
        }
      }
    }
  }
  function isBorderChunkInSet(cx: number, cy: number, set: Set<string>): boolean {
    return (
      !set.has(`${cx - 1},${cy}`) ||
      !set.has(`${cx + 1},${cy}`) ||
      !set.has(`${cx},${cy - 1}`) ||
      !set.has(`${cx},${cy + 1}`)
    );
  }

  function renderClaims(state: RenderState): void {
    const ctx = canvases.claims.getContext('2d');
    if (!ctx) return;
    const { view, claimBitmaps, inExpansion } = state;
    const cellPx = CELL_SIZE * view.zoom;
    const w = canvases.claims.width;
    const h = canvases.claims.height;

    // Claim overlay bitmaps
    for (const bm of [claimBitmaps.other, claimBitmaps.own]) {
      if (!bm) continue;
      const px = view.offsetX + bm.originX * cellPx;
      const py = view.offsetY + bm.originY * cellPx;
      const bw = bm.canvas.width * cellPx;
      const bh = bm.canvas.height * cellPx;
      if (px + bw < 0 || px > w || py + bh < 0 || py > h) continue;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bm.canvas, px, py, bw, bh);
    }

    if (!inExpansion) return;

    const {
      expandingChunks, pendingAdditions, pendingRemovals,
      validationErrors, islandChunks, disconnected,
    } = state;

    // Owned chunks (blue fill)
    ctx.fillStyle = 'rgba(88, 101, 242, 0.15)';
    for (const ck of expandingChunks) {
      if (pendingRemovals.has(ck)) continue;
      const [cx, cy] = ck.split(',').map(Number) as [number, number];
      const px = view.offsetX + cx * cellPx;
      const py = view.offsetY + cy * cellPx;
      if (px + cellPx < 0 || px > w || py + cellPx < 0 || py > h) continue;
      ctx.fillRect(px, py, cellPx, cellPx);
    }

    // Owned chunk borders
    ctx.strokeStyle = '#5865f2';
    ctx.lineWidth = 2;
    const cs = new Set([...expandingChunks, ...pendingAdditions]);
    for (const ck of pendingRemovals) cs.delete(ck);
    for (const ck of expandingChunks) {
      if (pendingRemovals.has(ck)) continue;
      const [cx, cy] = ck.split(',').map(Number) as [number, number];
      if (!isBorderChunkInSet(cx, cy, cs)) continue;
      const px = view.offsetX + cx * cellPx;
      const py = view.offsetY + cy * cellPx;
      if (px + cellPx < 0 || px > w || py + cellPx < 0 || py > h) continue;
      ctx.strokeRect(px, py, cellPx, cellPx);
    }

    // Pending additions (green)
    ctx.fillStyle = 'rgba(87, 242, 135, 0.4)';
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#57f287';
    ctx.lineWidth = 2;
    for (const ck of pendingAdditions) {
      const [cx, cy] = ck.split(',').map(Number) as [number, number];
      const px = view.offsetX + cx * cellPx;
      const py = view.offsetY + cy * cellPx;
      if (px + cellPx < 0 || px > w || py + cellPx < 0 || py > h) continue;
      ctx.fillRect(px, py, cellPx, cellPx);
      ctx.strokeRect(px, py, cellPx, cellPx);
    }
    ctx.setLineDash([]);

    // Pending removals (red strikethrough)
    ctx.fillStyle = 'rgba(237, 66, 69, 0.3)';
    ctx.strokeStyle = '#ed4245';
    ctx.lineWidth = 1;
    for (const ck of pendingRemovals) {
      const [cx, cy] = ck.split(',').map(Number) as [number, number];
      const px = view.offsetX + cx * cellPx;
      const py = view.offsetY + cy * cellPx;
      if (px + cellPx < 0 || px > w || py + cellPx < 0 || py > h) continue;
      ctx.fillRect(px, py, cellPx, cellPx);
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + cellPx, py + cellPx);
      ctx.moveTo(px + cellPx, py);
      ctx.lineTo(px, py + cellPx);
      ctx.stroke();
    }

    // Validation errors (orange)
    if (validationErrors.length > 0) {
      ctx.fillStyle = 'rgba(255, 165, 0, 0.5)';
      ctx.strokeStyle = '#ff8c00';
      ctx.lineWidth = 2;
      for (const c of validationErrors) {
        const px = view.offsetX + c.x * cellPx;
        const py = view.offsetY + c.y * cellPx;
        ctx.fillRect(px, py, cellPx, cellPx);
        ctx.strokeRect(px, py, cellPx, cellPx);
      }
    }

    // Island chunks (yellow)
    if (islandChunks.length > 0) {
      ctx.fillStyle = 'rgba(254, 231, 92, 0.5)';
      ctx.strokeStyle = '#fee75c';
      ctx.lineWidth = 2;
      for (const c of islandChunks) {
        const px = view.offsetX + c.x * cellPx;
        const py = view.offsetY + c.y * cellPx;
        ctx.fillRect(px, py, cellPx, cellPx);
        ctx.strokeRect(px, py, cellPx, cellPx);
      }
    }

    // Disconnected chunks (orange dashed border)
    if (disconnected.size > 0) {
      ctx.strokeStyle = '#ff8c00';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      for (const ck of disconnected) {
        const [cx, cy] = ck.split(',').map(Number) as [number, number];
        const px = view.offsetX + cx * cellPx;
        const py = view.offsetY + cy * cellPx;
        ctx.strokeRect(px, py, cellPx, cellPx);
      }
      ctx.setLineDash([]);
    }
  }
  const GRID_COLOR = 'rgba(255, 255, 255, 0.15)';
  const BLOCK_SIZE = 128;

  function isAdjacentToSet(
    cx: number, cy: number, ...sets: Set<string>[]
  ): boolean {
    const neighbors = [
      `${cx - 1},${cy}`, `${cx + 1},${cy}`,
      `${cx},${cy - 1}`, `${cx},${cy + 1}`,
    ];
    return neighbors.some((n) => sets.some((s) => s.has(n)));
  }

  function renderUI(state: RenderState): void {
    const ctx = canvases.ui.getContext('2d');
    if (!ctx) return;
    const { view, hoverChunk, selectedChunk, showGrid, inExpansion, showPanel } = state;
    const cellPx = CELL_SIZE * view.zoom;
    const w = canvases.ui.width;
    const h = canvases.ui.height;

    const startCx = Math.floor(-view.offsetX / cellPx);
    const startCy = Math.floor(-view.offsetY / cellPx);
    const endCx = Math.ceil((w - view.offsetX) / cellPx);
    const endCy = Math.ceil((h - view.offsetY) / cellPx);

    // Grid
    if (showGrid) {
      ctx.strokeStyle = GRID_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let cx = startCx; cx <= endCx; cx++) {
        const x = view.offsetX + cx * cellPx;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let cy = startCy; cy <= endCy; cy++) {
        const y = view.offsetY + cy * cellPx;
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();
    }

    // Expansion cursors
    if (inExpansion && hoverChunk) {
      const {
        expandingChunks, pendingAdditions, pendingRemovals,
        activeTool, brushSize, allClaimedSet,
        brushPreviewChunks, fillPreviewChunks,
      } = state;
      const ck = `${hoverChunk.x},${hoverChunk.y}`;
      const owned = expandingChunks.has(ck) || pendingAdditions.has(ck);
      const claimed = allClaimedSet();

      if (activeTool === 'eraser') {
        if (owned && !pendingRemovals.has(ck)) {
          ctx.strokeStyle = '#ed4245';
          ctx.lineWidth = 2;
          ctx.strokeRect(
            view.offsetX + hoverChunk.x * cellPx,
            view.offsetY + hoverChunk.y * cellPx,
            cellPx, cellPx,
          );
        }
      } else if (activeTool === 'brush' && brushSize > 1) {
        ctx.fillStyle = 'rgba(87, 242, 135, 0.2)';
        ctx.strokeStyle = '#57f287';
        ctx.lineWidth = 1;
        for (const c of brushPreviewChunks) {
          const bk = `${c.x},${c.y}`;
          if (expandingChunks.has(bk) || pendingAdditions.has(bk)) continue;
          if (claimed.has(bk) && !expandingChunks.has(bk)) continue;
          const px = view.offsetX + c.x * cellPx;
          const py = view.offsetY + c.y * cellPx;
          ctx.fillRect(px, py, cellPx, cellPx);
          ctx.strokeRect(px, py, cellPx, cellPx);
        }
      } else if (activeTool === 'fill') {
        if (fillPreviewChunks.length > 0) {
          ctx.fillStyle = 'rgba(87, 242, 135, 0.2)';
          for (const c of fillPreviewChunks) {
            const px = view.offsetX + c.x * cellPx;
            const py = view.offsetY + c.y * cellPx;
            ctx.fillRect(px, py, cellPx, cellPx);
          }
        }
      } else {
        // Brush (1x1) / Snap cursor
        const adj = isAdjacentToSet(hoverChunk.x, hoverChunk.y, expandingChunks, pendingAdditions);
        const free = !claimed.has(ck) || expandingChunks.has(ck);
        ctx.strokeStyle = adj && free && !owned ? '#57f287' : '#ed4245';
        ctx.lineWidth = 2;
        ctx.strokeRect(
          view.offsetX + hoverChunk.x * cellPx,
          view.offsetY + hoverChunk.y * cellPx,
          cellPx, cellPx,
        );
      }
    } else if (hoverChunk && !showPanel) {
      // Block highlight (128x128 parcel preview)
      const bx = Math.floor(hoverChunk.x / BLOCK_SIZE) * BLOCK_SIZE;
      const by = Math.floor(hoverChunk.y / BLOCK_SIZE) * BLOCK_SIZE;
      const blockPx = BLOCK_SIZE * cellPx;
      const overlaps = state.isBlockAreaClaimed(bx, by);
      ctx.fillStyle = overlaps ? 'rgba(237, 66, 69, 0.15)' : 'rgba(87, 242, 135, 0.15)';
      ctx.strokeStyle = overlaps ? '#ed4245' : '#57f287';
      ctx.lineWidth = 2;
      const px = view.offsetX + bx * cellPx;
      const py = view.offsetY + by * cellPx;
      ctx.fillRect(px, py, blockPx, blockPx);
      ctx.strokeRect(px, py, blockPx, blockPx);
    }

    // Selection highlight (yellow dashed)
    if (selectedChunk && !inExpansion) {
      ctx.strokeStyle = '#fee75c';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      const bx = Math.floor(selectedChunk.x / BLOCK_SIZE) * BLOCK_SIZE;
      const by = Math.floor(selectedChunk.y / BLOCK_SIZE) * BLOCK_SIZE;
      ctx.strokeRect(
        view.offsetX + bx * cellPx,
        view.offsetY + by * cellPx,
        BLOCK_SIZE * cellPx,
        BLOCK_SIZE * cellPx,
      );
      ctx.setLineDash([]);
    }
  }

  const renderers: Record<LayerName, (state: RenderState) => void> = {
    terrain: renderTerrain,
    hydro: renderHydro,
    claims: renderClaims,
    ui: renderUI,
  };

  function frame(state: RenderState): void {
    for (const name of layerOrder) {
      if (dirty[name]) {
        const ctx = canvases[name].getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvases[name].width, canvases[name].height);
          renderers[name](state);
        }
        dirty[name] = false;
      }
    }
  }

  function composite(view: ViewState): void {
    void view;
  }

  function destroy(): void {
    for (const name of layerOrder) {
      canvases[name].remove();
    }
  }

  return { invalidate, frame, composite, resize, chunkToScreen, destroy };
}
